// 对局流程：发牌 → 进贡还贡 → 出牌（含借风）→ 名次 → 升级 → 过 A。纯状态机，渲染层只读。
// 座位 0–3 逆时针出牌（座位号 +1 为下家）；0、2 一队，1、3 一队。
import { BIG_JOKER, isWild, makeDeck, power, rng, shuffle, sortHand } from './cards.ts';
import type { Card } from './cards.ts';
import { legalPlays } from './rules.ts';
import type { Play, PlayType } from './rules.ts';
import { RULES } from './ruleset.ts';
import type { Ruleset } from './ruleset.ts';

export type Seat = 0 | 1 | 2 | 3;
export const SEATS: Seat[] = [0, 1, 2, 3];
export const teamOf = (s: number): 0 | 1 => (s % 2) as 0 | 1;
export const partnerOf = (s: number): Seat => ((s + 2) % 4) as Seat;
export const nextSeat = (s: number): Seat => ((s + 1) % 4) as Seat;

export interface Tribute { from: Seat; to: Seat; card: Card; back?: Card }
export interface RoundResult { order: Seat[]; winnerTeam: 0 | 1; steps: number; passedA: boolean }
export type Action = { seat: Seat; play: Play | null };

export interface Round {
  level: number; // 本局打几（A = 14）
  levelTeam: 0 | 1; // 打的是哪一队的级
  hands: Card[][];
  turn: Seat;
  lead: Seat; // 本轮（一圈）谁先出
  last: { seat: Seat; play: Play } | null;
  passes: number;
  finished: Seat[];
  tributes: Tribute[];
  resisted: boolean;
  /** 首副牌翻出的定先牌。 */
  flipped?: Card;
  history: Action[];
  over: boolean;
}

export interface Match {
  rules: Ruleset;
  seed: number;
  teamLevel: [number, number];
  aFails: [number, number];
  roundNo: number;
  round: Round;
  results: RoundResult[];
  winner: 0 | 1 | null;
}

const active = (r: Round, s: Seat): boolean => !r.finished.includes(s);

function deal(seed: number): Card[][] {
  const deck = shuffle(makeDeck(), rng(seed));
  return SEATS.map((s) => deck.slice(s * 27, s * 27 + 27));
}

function newRound(hands: Card[][], level: number, levelTeam: 0 | 1, lead: Seat): Round {
  return { level, levelTeam, hands, turn: lead, lead, last: null, passes: 0, finished: [], tributes: [], resisted: false, history: [], over: false };
}

export function createMatch(seed: number, rules: Ruleset = RULES, dealt?: Card[][], random?: () => number): Match {
  const hands = (dealt ?? deal(seed)).map((hand) => [...hand]);
  const rand = random ?? rng(seed ^ 0x9e37);
  let first: Seat = 0;
  let flipped: Card | undefined;
  if (rules.firstLead === 'random') first = Math.floor(rand() * 4) as Seat;
  if (rules.firstLead === 'flip') {
    // 竞赛规则第七条：翻出王或红桃 2（首副牌的逢人配）就重翻；抓到这张牌的人首圈领出
    const all = hands.flat();
    do flipped = all[Math.floor(rand() * all.length)]; while (flipped.suit === 'J' || (flipped.suit === 'H' && flipped.rank === 2));
    first = hands.findIndex((h) => h.some((c) => c.id === flipped!.id)) as Seat;
  }
  const round = newRound(hands, 2, 0, first);
  round.flipped = flipped;
  return { rules, seed, teamLevel: [2, 2], aFails: [0, 0], roundNo: 1, round, results: [], winner: null };
}

/** 报牌：别人能看到的剩余张数；超过报牌线时返回 null（只知道“多于 N 张”）。 */
export const visibleCount = (m: Match, s: Seat): number | null => {
  const n = m.round.hands[s].length;
  return !m.rules.announceAt || n <= m.rules.announceAt ? n : null;
};

/** 当前轮到的人在这一圈要压的牌；null 表示自由出牌。 */
export const target = (r: Round): Play | null => r.last?.play ?? null;

/**
 * 出牌或过（cards 为空 = 过）。不合法抛错，状态不变。
 * 只认牌的 id：判型用手里的真牌，调用方传来的花色点数一律不信（联机时客户端只发 id）。
 * as：一手牌有多种合法出法时指定按哪种出（如两对加两张逢人配，可按三连对或三同连张）；不指定按默认。
 */
export function act(m: Match, seat: Seat, cards: Card[], as?: { type: PlayType; key: number }): Play | null {
  const r = m.round;
  if (r.over) throw new Error('本局已结束');
  if (seat !== r.turn) throw new Error(`还没轮到座位 ${seat}`);
  const hand = r.hands[seat];
  if (!cards.length) {
    if (!r.last) throw new Error('自由出牌时不能过');
    r.history.push({ seat, play: null });
    r.passes++;
    advance(m);
    return null;
  }
  const real = cards.map((c) => hand.find((h) => h.id === c.id));
  if (real.some((c) => !c) || new Set(cards.map((c) => c.id)).size !== cards.length) throw new Error('出的牌不在手里');
  const options = legalPlays(real as Card[], r.level, target(r));
  const play = as ? options.find((p) => p.type === as.type && p.key === as.key) : options[0];
  if (!play) throw new Error(as && options.length ? '不能按这种牌型出' : r.last ? '压不住上家' : '不成牌型');
  const ids = new Set(cards.map((c) => c.id));
  r.hands[seat] = hand.filter((c) => !ids.has(c.id));
  r.last = { seat, play };
  r.passes = 0;
  r.history.push({ seat, play });
  if (!r.hands[seat].length) r.finished.push(seat);
  if (roundDone(r)) finishRound(m);
  else advance(m);
  return play;
}

/** 一队两人都出完，本局结束。 */
function roundDone(r: Round): boolean {
  return [0, 1].some((t) => r.finished.filter((s) => teamOf(s) === t).length === 2);
}

function advance(m: Match): void {
  const r = m.round;
  const owner = r.last!.seat;
  const needed = SEATS.filter((s) => active(r, s) && s !== owner).length;
  if (r.passes >= needed) {
    // 一圈结束：出牌人还在就由他接着出；已出完则由搭档借风
    const lead = active(r, owner) ? owner : partnerOf(owner);
    r.last = null;
    r.passes = 0;
    r.lead = lead;
    r.turn = lead;
    return;
  }
  let s = nextSeat(r.turn);
  while (!active(r, s)) s = nextSeat(s);
  r.turn = s;
}

function finishRound(m: Match): void {
  const r = m.round;
  r.over = true;
  const rest = SEATS.filter((s) => active(r, s));
  const order = [...r.finished, ...rest] as Seat[]; // 剩下的人按座位排，不影响升级
  const head = order[0];
  const winnerTeam = teamOf(head);
  const partnerPlace = order.indexOf(partnerOf(head)) + 1; // 2/3/4
  const steps = m.rules.upgrade[partnerPlace as 2 | 3 | 4];
  let passedA = false;
  const target = m.rules.target ?? 14; // 目标级：默认 A；短局赛制可以是 6 / 10（规则同过 A）
  const playingOwnA = r.levelTeam === winnerTeam && m.teamLevel[winnerTeam] === target;
  if (playingOwnA && m.rules.passA.allowedPartnerPlaces.includes(partnerPlace)) {
    passedA = true;
    m.winner = winnerTeam;
  } else {
    if (playingOwnA) {
      m.aFails[winnerTeam]++;
    }
    m.teamLevel[winnerTeam] = Math.min(target, m.teamLevel[winnerTeam] + steps);
  }
  // 打 A 的一方没赢（对方拿上游）也算一次失败
  const loser = (1 - winnerTeam) as 0 | 1;
  if (r.levelTeam === loser && m.teamLevel[loser] === target) m.aFails[loser]++;
  for (const t of [0, 1] as const) {
    if (m.rules.passA.resetAfterFails && m.aFails[t] >= m.rules.passA.resetAfterFails) {
      m.teamLevel[t] = 2;
      m.aFails[t] = 0;
    }
  }
  m.results.push({ order, winnerTeam, steps, passedA });
  // 限副赛：打满规定副数还没人过 A，级数高者胜；同级由这一副拿上游的一方胜（暂定，见 ruleset.ts）
  if (m.winner === null && m.rules.roundLimit && m.results.length >= m.rules.roundLimit) {
    const [a, b] = m.teamLevel;
    m.winner = a === b ? winnerTeam : a > b ? 0 : 1;
  }
}

/** 进贡选牌：最大的一张，红桃级牌除外。 */
export function tributeCard(hand: Card[], level: number): Card {
  return sortHand(hand.filter((c) => !isWild(c, level)), level)[0];
}

/**
 * 还牌可选的牌：牌点不超过上限（默认 10）。牌点按竞赛规则第四条的顺序算——级牌排在 A 之上，
 * 所以级牌（含红桃逢人配）不能用来还牌。全手牌都超过上限时，只能还最小的一张。
 */
export function returnCandidates(hand: Card[], level: number, limit: number): Card[] {
  const ok = hand.filter((c) => power(c.rank, level) <= limit);
  return ok.length ? ok : sortHand(hand, level).slice(-1);
}

/** 默认还牌：可选牌里最小的一张。 */
export const defaultReturn = (options: Card[], level: number): Card => sortHand(options, level).at(-1)!;

/**
 * 开下一局：按上一局名次定级与进贡。chooseReturn 让界面或机器人决定还哪张（默认还最小）。
 * 单机按种子复现；联机可注入服务端安全洗牌的 dealt，测试也可用它摆固定牌面。
 */
export function nextRound(m: Match, chooseReturn?: (seat: Seat, options: Card[]) => Card, dealt?: Card[][]): void {
  if (m.winner !== null) throw new Error('比赛已结束');
  const prev = m.results.at(-1);
  if (!prev || !m.round.over) throw new Error('上一局还没结束');
  // 先在局部算完发牌、进贡、还牌并全部校验，最后一步才写回 m：任何一步抛错，对局状态都不变
  const roundNo = m.roundNo + 1;
  const levelTeam = prev.winnerTeam;
  const level = m.teamLevel[levelTeam];
  const hands = (dealt ?? deal(m.seed + roundNo * 7919)).map((h) => [...h]); // dealt：测试摆固定牌面
  const [head, second, , last] = prev.order;
  const doubleDown = teamOf(head) === teamOf(second);
  const payers = doubleDown ? [prev.order[2], last] : [last];
  const bigJokers = payers.reduce((n: number, s) => n + hands[s].filter((c) => c.rank === BIG_JOKER).length, 0);
  const round = newRound(hands, level, levelTeam, head);
  if (bigJokers >= 2 && m.rules.tribute.resistWithTwoBigJokers) {
    round.resisted = true; // 抗贡：上游先出
  } else {
    const paid = payers.map((s) => ({ from: s, card: tributeCard(hands[s], level) }));
    // 双贡：大的给上游，小的给上游的搭档；牌点相同按顺时针方向进贡（竞赛规则第十条三），
    // 即上游的下家（逆时针出牌时顺时针回到上游的那一位）贡给上游
    if (doubleDown) {
      const dist = (s: Seat) => (s - head + 4) % 4;
      paid.sort((a, b) => power(b.card.rank, level) - power(a.card.rank, level) || dist(a.from) - dist(b.from));
    }
    const receivers: Seat[] = doubleDown ? [head, second] : [head];
    paid.forEach((p, i) => {
      const to = receivers[i];
      move(hands, p.from, to, p.card);
      const options = returnCandidates(hands[to], level, m.rules.tribute.returnMaxRank);
      const back = chooseReturn ? chooseReturn(to, options) : defaultReturn(options, level);
      if (!options.some((c) => c.id === back?.id)) throw new Error('还的牌不合规');
      move(hands, to, p.from, back);
      round.tributes.push({ from: p.from, to, card: p.card, back });
    });
    // 进贡后由进贡的人先出；双贡时进贡给上游的人先出
    round.lead = round.turn = paid[0].from;
  }
  m.roundNo = roundNo;
  m.round = round;
}

/** 某个座位能看到的信息：自己的手牌、桌面、报牌公开的张数、游数。机器人只拿这个，不拿整个对局。 */
export interface PlayerView {
  seat: Seat;
  level: number;
  hand: Card[];
  last: { seat: Seat; play: Play } | null;
  /** 各座位公开的剩余张数；超过报牌线为 null。 */
  counts: (number | null)[];
  finished: Seat[];
  /** 本副已经打出的牌（桌上公开，记牌用）。 */
  played: Card[];
}

export function viewOf(m: Match, seat: Seat): PlayerView {
  const r = m.round;
  return {
    seat, level: r.level, hand: [...r.hands[seat]], last: r.last,
    counts: SEATS.map((s) => (s === seat ? r.hands[s].length : visibleCount(m, s))),
    finished: [...r.finished],
    played: r.history.flatMap((a) => a.play?.cards ?? []),
  };
}

function move(hands: Card[][], from: Seat, to: Seat, card: Card): void {
  hands[from] = hands[from].filter((c) => c.id !== card.id);
  hands[to].push(card);
}
